import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { ErrorFilters } from "./ErrorFilters";
import { ErrorList } from "./ErrorList";
import { ErrorDetail } from "./ErrorDetail";
import { EmptyState } from "../../ui/EmptyState/EmptyState";
import { Button } from "../../ui/Button/Button";
import { Icon } from "../../ui/Icon/Icon";
import { useBreakpoint } from "../../lib/useBreakpoint";
import { useShellChrome } from "../../shell/shellChrome";
import {
  clearSelection,
  useErrorsStore,
  useRankedProblems,
  useSelectedCode,
} from "./store";
import styles from "./ErrorExplorer.module.css";

const SCREEN_MS = 260;

type Screen = "list" | "detail";

// Mirrors the filter bar + rows so the loaded list barely shifts.
const ROW_TITLES = [52, 64, 44, 58, 48, 62, 40];
const ROW_CODES = [34, 28, 40, 30, 36, 26, 38];

function ListSkeleton(): ReactElement {
  return (
    <SkeletonTheme
      baseColor="var(--color-neutral-01)"
      highlightColor="var(--color-neutral-02)"
      borderRadius={8}
    >
      <div className={styles.controls} aria-hidden="true">
        <Skeleton height={44} borderRadius={12} />
        <div className={styles.skChips}>
          {[60, 88, 76, 84, 70].map((w, i) => (
            <Skeleton key={i} width={w} height={30} borderRadius={999} />
          ))}
        </div>
      </div>
      <div aria-hidden="true">
        {ROW_TITLES.map((w, i) => (
          <div key={i} className={styles.skItem}>
            <div className={styles.skItemMain}>
              <Skeleton width={`${w}%`} height={16} />
              <Skeleton width={`${ROW_CODES[i]}%`} height={12} />
            </div>
            <Skeleton width={46} height={22} borderRadius={6} />
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
}

// Mirrors ErrorDetail below the (persistent) back button: title, code + share,
// three stats, two text sections, and the dark JSON reference block.
function DetailSkeleton(): ReactElement {
  return (
    <div className={styles.detailSk}>
      <SkeletonTheme
        baseColor="var(--color-neutral-01)"
        highlightColor="var(--color-neutral-02)"
        borderRadius={6}
      >
        <div className={styles.detailSkBody} aria-hidden="true">
          <Skeleton width="55%" height={28} />
          <div className={styles.skSubhead}>
            <Skeleton width={170} height={16} />
            <Skeleton width={112} height={30} borderRadius={8} />
          </div>
          <div className={styles.skStats}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.skStat}>
                <Skeleton width="60%" height={10} />
                <Skeleton width="82%" height={16} />
              </div>
            ))}
          </div>
          {[
            [88, 54],
            [92, 40],
          ].map((lines, s) => (
            <div key={s} className={styles.skSection}>
              <Skeleton width={110} height={10} />
              <div className={styles.skPara}>
                {lines.map((w, i) => (
                  <Skeleton key={i} width={`${w}%`} height={13} />
                ))}
              </div>
            </div>
          ))}
          <div className={styles.skRefHead}>
            <Skeleton width={210} height={10} />
            <Skeleton width={112} height={30} borderRadius={8} />
          </div>
        </div>
      </SkeletonTheme>
      <SkeletonTheme
        baseColor="var(--color-brand-secondary-08)"
        highlightColor="var(--color-brand-secondary-07)"
        borderRadius={5}
      >
        <div className={styles.skCodeBlock} aria-hidden="true">
          {[40, 74, 58, 86, 34, 66, 50].map((w, i) => (
            <Skeleton key={i} width={`${w}%`} height={11} />
          ))}
        </div>
      </SkeletonTheme>
    </div>
  );
}

function StatusMessage({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): ReactElement {
  return (
    <div className={styles.status} role="status">
      <p className={styles.statusTitle}>{title}</p>
      {children ? <div className={styles.statusBody}>{children}</div> : null}
    </div>
  );
}

type SlideDir = "forward" | "back";

// Transition between two keyed views, reused for the screen (list↔detail, a
// directional slide) and the detail body (skeleton↔content, a crossfade). The
// entering view is in flow; the outgoing one overlays and animates out.
function useSlideStage(
  key: string,
  view: ReactNode,
  dirFor: (from: string, to: string) => SlideDir | null,
  variant: "slide" | "fade" = "slide",
): ReactElement {
  const snapshots = useRef<Record<string, ReactNode>>({});
  snapshots.current[key] = view;
  const prev = useRef(key);
  const [leaving, setLeaving] = useState<{
    view: ReactNode;
    dir: SlideDir;
  } | null>(null);

  useEffect(() => {
    const from = prev.current;
    if (from === key) return;
    prev.current = key;
    const dir = dirFor(from, key);
    if (!dir) return;
    setLeaving({ view: snapshots.current[from] ?? null, dir });
    const timer = setTimeout(() => setLeaving(null), SCREEN_MS);
    return () => clearTimeout(timer);
  }, [key, dirFor]);

  const enterClass = !leaving
    ? ""
    : variant === "fade"
      ? styles.enterFade
      : leaving.dir === "forward"
        ? styles.enterForward
        : styles.enterBack;
  const leaveClass =
    variant === "fade"
      ? styles.leaveFade
      : leaving?.dir === "forward"
        ? styles.leaveForward
        : styles.leaveBack;

  return (
    <div className={styles.stage}>
      <div key={key} className={`${styles.screen} ${enterClass}`}>
        {view}
      </div>
      {leaving ? (
        <div
          className={`${styles.screen} ${styles.leaving} ${leaveClass}`}
          aria-hidden="true"
        >
          {leaving.view}
        </div>
      ) : null}
    </div>
  );
}

const screenDir = (_from: string, to: string): SlideDir =>
  to === "detail" ? "forward" : "back";
const bodyDir = (from: string, to: string): SlideDir | null => {
  if (from === "loading" && to === "loaded") return "forward";
  if (from === "loaded" && to === "loading") return "back";
  return null;
};

export interface ErrorExplorerProps {
  onOpenReference?: () => void;
}

export function ErrorExplorer({
  onOpenReference,
}: ErrorExplorerProps): ReactElement {
  const { isMobile } = useBreakpoint();
  const { setBack } = useShellChrome();

  const code = useSelectedCode();
  const screen: Screen = code ? "detail" : "list";

  const indexStatus = useErrorsStore((state) => state.indexStatus);
  const docStatus = useErrorsStore((state) =>
    code ? (state.docStatus[code] ?? "idle") : "idle",
  );
  const hasDoc = useErrorsStore((state) =>
    code ? Boolean(state.docs[code]) : false,
  );
  const loadIndex = useErrorsStore((state) => state.loadIndex);
  const loadDocument = useErrorsStore((state) => state.loadDocument);
  const clearFilters = useErrorsStore((state) => state.clearFilters);
  const ranked = useRankedProblems();

  const back = clearSelection;

  useEffect(() => {
    void loadIndex();
  }, [loadIndex]);

  useEffect(() => {
    if (code) void loadDocument(code);
  }, [code, loadDocument]);

  useEffect(() => {
    if (isMobile && code) {
      setBack(back);
      return () => setBack(null);
    }
  }, [isMobile, code, setBack, back]);

  const indexPending = indexStatus === "idle" || indexStatus === "loading";
  const detailLoading =
    indexPending || docStatus === "idle" || docStatus === "loading";
  const detailBody = detailLoading ? (
    <DetailSkeleton />
  ) : docStatus === "error" ? (
    <StatusMessage title="Couldn't load this error">
      <Button
        variant="secondary"
        onClick={() => code && void loadDocument(code)}
      >
        Try again
      </Button>
    </StatusMessage>
  ) : hasDoc ? (
    <ErrorDetail onOpenReference={onOpenReference} />
  ) : (
    <StatusMessage title="We couldn't find that error code">
      <Button variant="secondary" onClick={back}>
        Back to all errors
      </Button>
    </StatusMessage>
  );

  // Slide skeleton→content with the same directional slide as list→detail. The
  // back button sits outside this stage, so it stays put while the body slides.
  const bodyStage = useSlideStage(
    code ? (detailLoading ? "loading" : "loaded") : "none",
    detailBody,
    bodyDir,
    "fade",
  );

  const detailView = (
    <div className={styles.card}>
      <div className={styles.detailWrap}>
        <button type="button" className={styles.backOnly} onClick={back}>
          <Icon name="chevron" size={16} className={styles.backIcon} />
          All errors
        </button>
        {bodyStage}
      </div>
    </div>
  );

  const listView = (
    <>
      {indexPending ? (
        <div className={styles.card}>
          <ListSkeleton />
        </div>
      ) : indexStatus === "error" ? (
        <div className={styles.card}>
          <StatusMessage title="Couldn't load the error catalogue">
            <Button variant="secondary" onClick={() => void loadIndex()}>
              Try again
            </Button>
          </StatusMessage>
        </div>
      ) : (
        <>
          <div className={styles.card}>
            <div className={styles.controls}>
              <ErrorFilters />
            </div>
            {ranked.length === 0 ? null : <ErrorList />}
          </div>
          {ranked.length === 0 ? (
            <EmptyState
              title="No errors match your search"
              action={
                <Button variant="secondary" onClick={() => clearFilters()}>
                  Clear filters
                </Button>
              }
            >
              Try an error code, part of a title, or a status like 404.
            </EmptyState>
          ) : null}
        </>
      )}
    </>
  );

  const screenStage = useSlideStage(
    screen,
    code ? detailView : listView,
    screenDir,
  );

  return (
    <div className={styles.explorer}>
      <div>
        <h1 className={styles.title}>Explore errors</h1>
        <p className={styles.intro}>
          Every error the API can return. Search by code, title or
          status, then open a row for the full reference.
        </p>
      </div>
      {screenStage}
    </div>
  );
}
